import {
  Alert,
  Anchor,
  Button,
  ButtonGroup,
  DetailedIconMessageHeart,
  DetailedIconPaperAirplane,
  Flex,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  ModalDialog,
  Paragraph,
  Text,
  Textarea,
  TextButton,
  Trigger,
} from '@zillow/constellation';
import { useForm } from 'react-hook-form';
import { APPLICATIONS_FAQ_URL, EXTERNAL_URLS, getExternalUrl } from '../../constants/urls';
import useSendConversationMessage from '../../hooks/useSendConversationMessage';
import { useState } from 'react';
import useGetConversationInfoIfExists from '../../hooks/useGetConversationInfoIfExists';
import type { RentalApplication } from '../../graphql/queries';
import { PhoneButton } from '../Buttons/PhoneButton';
import { MinHeightLoadingMask } from '../../styles/styled-components';

type Props = {
  application: RentalApplication;
};

type ContactPropertyFormData = {
  firstName: string;
  lastName: string;
  message: string;
};

const nameMaxLengthValidation = { value: 32, message: 'Please enter a name under 32 characters' };
const messageMaxLengthValidation = { value: 10000, message: 'Please enter a message under 10,000 characters' };

export function ContactProperty({ application }: Props) {
  const Modal = ({ isOpen }: { isOpen: boolean }, close?: () => void) =>
    isOpen && <ContactPropertyModal application={application} onClose={close} />;

  return (
    <Trigger triggered={Modal}>
      <TextButton>Contact property</TextButton>
    </Trigger>
  );
}

type ModalProps = {
  application: RentalApplication;
  onClose?: () => void;
};

function ContactPropertyModal({ application, onClose }: ModalProps) {
  const { rentalListing, listingOwner, answers } = application || {};
  const { firstName: renterFirstName, lastName: renterLastName } = answers || {};
  const { address, listingAlias, propertyUrl } = rentalListing || {};

  const applicationId = application.id || '';

  const { data: apiConversationId, isPending } = useGetConversationInfoIfExists(listingAlias || '');
  const [sentConversationId, setSentConversationId] = useState<string>();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactPropertyFormData>();

  const {
    mutate: sendConversationMessage,
    error,
    isPending: isSending,
  } = useSendConversationMessage({
    onSuccess: (conversationId: string) => setSentConversationId(conversationId),
  });

  async function onSendConversationMessage(formData: ContactPropertyFormData) {
    sendConversationMessage({
      listingAlias: listingAlias || '',
      textBody: formData.message,
      applicationId,
      renterName: `${formData.firstName} ${formData.lastName || ''}`.trim(),
    });
  }

  const conversationId = apiConversationId || sentConversationId;
  const addressDisplay = `${address?.street} ${address?.city}, ${address?.state} ${address?.zip}`;

  return (
    <ModalDialog
      isOpen
      onClose={onClose}
      size={{ default: 'fullScreen', md: 'sm' }}
      renderHeader={<Heading level={1}>Contact property</Heading>}
      renderBody={
        <MinHeightLoadingMask loading={isPending || isSending}>
          {!isPending && (
            <Flex display="flex" flexDirection="column">
              {error && (
                <Alert
                  appearance="error"
                  marginBottom="sm"
                  body={
                    <Text>
                      The message can’t be delivered right now. Please try again. If the problem continues, contact{' '}
                      <Anchor href={APPLICATIONS_FAQ_URL} target="_blank">
                        customer support
                      </Anchor>
                      .
                    </Text>
                  }
                />
              )}
              {conversationId ? (
                <DetailedIconPaperAirplane status="success" marginTop="xs" marginBottom="sm" />
              ) : (
                <DetailedIconMessageHeart marginTop="xs" marginBottom="sm" />
              )}
              <Heading level={4} as="h2" fontFamily="serif">
                {conversationId ? `Your message was sent` : `You are starting a new conversation`}
              </Heading>
              <Paragraph marginTop="sm" fontType="bodySmall">
                Property address:{' '}
                {propertyUrl ? (
                  <TextButton as="a" href={propertyUrl as string}>
                    {addressDisplay}
                  </TextButton>
                ) : (
                  <strong>{addressDisplay}</strong>
                )}
              </Paragraph>
              <Paragraph marginTop="sm" fontType="bodySmall">
                Contact: <strong>{listingOwner?.name}</strong>
              </Paragraph>
              <PhoneButton phoneNumber={listingOwner?.phone} />
              {conversationId ? (
                <Paragraph marginY="sm">
                  You can track all your conversations on your personalized Zillow{' '}
                  <Anchor href={getExternalUrl(EXTERNAL_URLS.RENTER_HUB)} target="_blank">
                    renting page
                  </Anchor>
                  .
                </Paragraph>
              ) : (
                <>
                  <FormField
                    marginBottom="sm"
                    label={<Label>First name</Label>}
                    control={
                      <Input
                        {...register('firstName', {
                          required: 'First name is required',
                          maxLength: nameMaxLengthValidation,
                          value: renterFirstName || '',
                        })}
                      />
                    }
                    description={errors.firstName && <FormHelp>{errors.firstName.message}</FormHelp>}
                    error={!!errors.firstName}
                  />
                  <FormField
                    marginBottom="sm"
                    label={<Label>Last name</Label>}
                    optional
                    control={
                      <Input
                        {...register('lastName', { maxLength: nameMaxLengthValidation, value: renterLastName || '' })}
                      />
                    }
                    description={errors.lastName && <FormHelp>{errors.lastName.message}</FormHelp>}
                    error={!!errors.lastName}
                  />
                  <FormField
                    marginBottom="xs"
                    label={<Label>Message</Label>}
                    control={
                      <Textarea
                        resize="vertical"
                        {...register('message', {
                          required: 'Message is required',
                          maxLength: messageMaxLengthValidation,
                        })}
                      />
                    }
                    description={errors.message && <FormHelp>{errors.message.message}</FormHelp>}
                    error={!!errors.message}
                  />
                  <Text fontType="legal">
                    After sending this message, you’ll be able to continue the conversation from your{' '}
                    <Anchor href={getExternalUrl(EXTERNAL_URLS.RENTER_HUB)} target="_blank">
                      renting page
                    </Anchor>{' '}
                    or directly from your email.
                  </Text>
                </>
              )}
            </Flex>
          )}
        </MinHeightLoadingMask>
      }
      renderFooter={
        isPending ? null : conversationId ? (
          <ButtonGroup aria-label="View message actions">
            <TextButton onClick={onClose}>Close</TextButton>
            <Button
              as="a"
              buttonType="primary"
              href={getExternalUrl(`${EXTERNAL_URLS.CONVERSATION}${conversationId}`)}
              target="_blank"
            >
              See messages
            </Button>
          </ButtonGroup>
        ) : (
          <ButtonGroup aria-label="Message landlord actions">
            <TextButton onClick={onClose} disabled={isSending} type="button">
              Cancel
            </TextButton>
            <Button
              buttonType="primary"
              onClick={handleSubmit(onSendConversationMessage)}
              disabled={isSending}
              type="button"
            >
              Send message
            </Button>
          </ButtonGroup>
        )
      }
    />
  );
}
